import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, TriangleAlert } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import { ConfirmErase } from '../components/ConfirmErase';
import { eraseSelf, exportOwnData } from '../lib/members';
import { logout } from '../lib/session';

type Export = 'idle' | 'working' | 'done' | 'error';

/**
 * Spec 004 — a member's data rights (SC-418/419/420). Export downloads a machine-readable copy of the
 * member's own data; erasure is a deliberate, typed-confirmation flow. On successful erasure the session
 * is cleared and the person is sent to the home page. WCAG 2.1 AA.
 */
function PrivacyInner() {
  const navigate = useNavigate();
  const [exp, setExp] = useState<Export>('idle');
  const [showErase, setShowErase] = useState(false);

  async function onExport() {
    if (exp === 'working') return;
    setExp('working');
    const r = await exportOwnData();
    setExp(r.kind === 'ok' ? 'done' : 'error');
  }

  async function afterErase() {
    await logout().catch(() => undefined); // best-effort; the account is already disabled server-side
    navigate('/', { replace: true });
  }

  return (
    <div className="mx-auto w-full max-w-xl px-6 py-16 sm:py-20">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">Your data</p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">Privacy</h1>
      <p className="mt-4 font-serif text-lg leading-relaxed text-muted">
        You own your data. Download a copy at any time, or ask us to erase it.
      </p>

      <section aria-labelledby="export-heading" className="mt-10 border-t border-border pt-8">
        <h2 id="export-heading" className="font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">Export your data</h2>
        <p className="mt-2 font-serif text-base leading-relaxed text-muted">
          A machine-readable file with your profile, account details, consent record, and your original
          application. Only your own data — nothing about other members.
        </p>
        {exp === 'error' && (
          <p role="alert" className="mt-4 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
            <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            We couldn’t prepare your export. Please try again.
          </p>
        )}
        {exp === 'done' && (
          <p role="status" className="mt-4 flex items-center gap-2 font-sans text-sm text-success">
            <CheckCircle2 aria-hidden className="h-4 w-4" /> Your download has started.
          </p>
        )}
        <button
          type="button"
          onClick={onExport}
          disabled={exp === 'working'}
          className="mt-4 inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 font-sans text-sm font-semibold text-ink transition-colors duration-150 ease-standard hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
        >
          {exp === 'working' ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <Download aria-hidden className="h-4 w-4" />}
          Download my data
        </button>
      </section>

      <section aria-labelledby="erase-heading" className="mt-10 border-t border-border pt-8">
        <h2 id="erase-heading" className="font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">Erase your data</h2>
        <p className="mt-2 font-serif text-base leading-relaxed text-muted">
          Permanently remove your profile and personal details and close your account. This can’t be undone.
        </p>
        <div className="mt-4">
          {showErase ? (
            <ConfirmErase subject="your account" onErase={(confirm) => eraseSelf(confirm)} onDone={afterErase} />
          ) : (
            <button
              type="button"
              onClick={() => setShowErase(true)}
              className="font-sans text-sm font-medium text-error underline underline-offset-4 hover:text-error/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error"
            >
              Erase my data…
            </button>
          )}
        </div>
      </section>

      <p className="mt-10 border-t border-border pt-6 font-sans text-sm text-muted">
        <Link to="/profile" className="text-accent-deep underline underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">← Back to your profile</Link>
      </p>
    </div>
  );
}

export default function Privacy() {
  return <RequireRole min="member">{() => <PrivacyInner />}</RequireRole>;
}
